"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { useTheme } from "next-themes";

/**
 * Global keyboard shortcuts for the reader screen.
 *
 * - Tab / Shift+Tab            cycle selection mode forward / backward
 * - ArrowLeft / ArrowRight     move selection prev / next (in current mode)
 * - ArrowUp / PageUp           scroll reader up
 * - ArrowDown / PageDown / Space  scroll reader down (Space pages)
 * - Home / End                 scroll to top / bottom
 * - + / =                      increase font size
 * - - / _                      decrease font size
 * - D                          toggle dark mode
 * - T                          speak current selection (TTS)
 * - S                          save current word to vocabulary
 * - M                          open settings menu
 * - ?                          show keyboard shortcuts help
 * - Esc                        go home (with confirm if mid-text)
 *
 * The hook accepts callbacks for actions that need component-level wiring
 * (open settings, open shortcuts, speak, save).
 */
type Options = {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onOpenSettings: () => void;
  onOpenShortcuts: () => void;
  onSpeak: () => void;
  onSaveWord: () => void;
  enabled?: boolean;
};

export function useKeyboardShortcuts({
  scrollContainerRef,
  onOpenSettings,
  onOpenShortcuts,
  onSpeak,
  onSaveWord,
  enabled = true,
}: Options) {
  const cycleMode = useStore((s) => s.cycleSelectionMode);
  const reverseCycleMode = useStore((s) => s.reverseCycleSelectionMode);
  const moveSelection = useStore((s) => s.moveSelection);
  const setSettings = useStore((s) => s.setSettings);
  const settings = useStore((s) => s.settings);
  const { setTheme, theme } = useTheme();

  React.useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      // Don't hijack typing in form fields
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.getAttribute("role") === "textbox")
      ) {
        return;
      }

      // Modifier-key combos: ignore plain shortcuts if Ctrl/Meta held
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey;

      // Tab cycles selection mode
      if (e.key === "Tab") {
        e.preventDefault();
        if (e.shiftKey) reverseCycleMode();
        else cycleMode();
        return;
      }

      // Arrow navigation: left/right move selection, up/down scroll
      if (e.key === "ArrowRight" && !hasModifier) {
        e.preventDefault();
        moveSelection("next");
        return;
      }
      if (e.key === "ArrowLeft" && !hasModifier) {
        e.preventDefault();
        moveSelection("prev");
        return;
      }
      if ((e.key === "ArrowUp" || e.key === "PageUp") && !hasModifier) {
        e.preventDefault();
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollBy({
            top: -el.clientHeight * 0.85,
            behavior: e.key === "PageUp" ? "auto" : "smooth",
          });
        }
        return;
      }
      if ((e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") && !hasModifier) {
        e.preventDefault();
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollBy({
            top: el.clientHeight * 0.85,
            behavior: e.key === "PageDown" || e.key === " " ? "auto" : "smooth",
          });
        }
        return;
      }
      if (e.key === "Home" && !hasModifier) {
        e.preventDefault();
        const el = scrollContainerRef.current;
        if (el) el.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (e.key === "End" && !hasModifier) {
        e.preventDefault();
        const el = scrollContainerRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        return;
      }

      // Font size +/-
      if ((e.key === "+" || e.key === "=") && !hasModifier) {
        e.preventDefault();
        setSettings({ fontSize: Math.min(40, settings.fontSize + 1) });
        return;
      }
      if ((e.key === "-" || e.key === "_") && !hasModifier) {
        e.preventDefault();
        setSettings({ fontSize: Math.max(14, settings.fontSize - 1) });
        return;
      }

      // Toggle dark mode
      if (e.key.toLowerCase() === "d" && !hasModifier) {
        e.preventDefault();
        setTheme(theme === "dark" ? "light" : "dark");
        return;
      }

      // TTS speak
      if (e.key.toLowerCase() === "t" && !hasModifier) {
        e.preventDefault();
        onSpeak();
        return;
      }

      // Save current word
      if (e.key.toLowerCase() === "s" && !hasModifier) {
        e.preventDefault();
        onSaveWord();
        return;
      }

      // Open settings menu
      if (e.key.toLowerCase() === "m" && !hasModifier) {
        e.preventDefault();
        onOpenSettings();
        return;
      }

      // Show shortcuts help
      if (e.key === "?" && !hasModifier) {
        e.preventDefault();
        onOpenShortcuts();
        return;
      }
    };

    window.addEventListener("keydown", handler, { capture: false });
    return () => window.removeEventListener("keydown", handler);
  }, [
    enabled,
    cycleMode,
    reverseCycleMode,
    moveSelection,
    setSettings,
    settings.fontSize,
    theme,
    setTheme,
    scrollContainerRef,
    onOpenSettings,
    onOpenShortcuts,
    onSpeak,
    onSaveWord,
  ]);
}
